/**
 * Custom Overlay Factory
 *
 * Creates custom Google Maps overlays that can render React components.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';

/**
 * CustomOverlay constructor
 * @param {google.maps.Map} map - Google Maps instance
 * @param {Object} config - Configuration object
 */
function CustomOverlay(map, config) {
	this.config = config;
	this.setMap(map);
}

/**
 * Create a custom overlay on a Google Map
 *
 * @param {Object} google - Google Maps API object
 * @param {google.maps.Map} map - Google Maps instance
 * @param {Object} config - Configuration options
 * @param {Object} config.location - Location { latitude, longitude }
 * @param {string} [config.className] - CSS class name
 * @param {React.ReactNode} [config.label] - React content to render
 * @returns {CustomOverlay} Custom overlay instance
 */
export default function CreateCustomOverlay(google, map, config) {
	CustomOverlay.prototype = new google.maps.OverlayView();

	/**
	 * Remove the overlay from the map
	 */
	CustomOverlay.prototype.remove = function remove() {
		if (this.div) {
			ReactDOM.unmountComponentAtNode(this.div);
			this.div.parentNode.removeChild(this.div);
			this.div = null;
			this.setMap(null);
		}
	};

	/**
	 * Update overlay configuration
	 * @param {Object} newConfig - New configuration to merge
	 */
	CustomOverlay.prototype.update = function update(newConfig) {
		this.config = { ...this.config, ...newConfig };
		if (this.div) {
			this.div.className = classnames(
				'custom-marker',
				this.config.className,
			);
			// Re-render label content if it changed
			if (this.config.label !== undefined) {
				ReactDOM.render(<div>{this.config.label}</div>, this.div);
			}
		}
	};

	/**
	 * Draw the overlay on the map
	 */
	CustomOverlay.prototype.draw = function draw() {
		let { div } = this;

		if (!div) {
			div = document.createElement('div');
			div.className = classnames('custom-marker', this.config.className);

			// Click handler: scroll to corresponding facility card when marker is clicked
			if (this.config.onClick) {
				div.style.cursor = 'pointer';
				div.addEventListener('click', (e) => {
					e.stopPropagation();
					this.config.onClick(this.config.location);
				});
			}

			// Hover handlers: highlight corresponding facility card when hovering on marker
			if (this.config.onHover) {
				div.addEventListener('mouseenter', () => {
					this.config.onHover(this.config.location);
				});
				div.addEventListener('mouseleave', () => {
					this.config.onHover(null);
				});
			}

			const panes = this.getPanes();
			panes.overlayImage.appendChild(div);

			if (this.config.label) {
				ReactDOM.render(<div>{this.config.label}</div>, div);
			}

			this.div = div;
		}

		const point = this.getProjection().fromLatLngToDivPixel(
			this.config.position,
		);
		// Use optional chaining instead of lodash.get
		div.style.left = `${point?.x ?? 0}px`;
		div.style.top = `${point?.y ?? 0}px`;
	};

	return new CustomOverlay(map, {
		...config,
		position: new google.maps.LatLng(
			config.location.latitude,
			config.location.longitude,
		),
	});
}
